/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { Component } from 'react';

import {
  Button,
  FilePickerButton,
  HStack,
  Input,
  InputGroup,
  InputRightElement,
  Progress,
  Tooltip,
  VStack,
} from '../../../../core-ui';
import { Alert, Icon, Text } from '../../../uiold';
import Thumbnail from '../../../Preview/Thumbnail';
import FileUpload, { Response } from '../../../../support/FileUpload';
import { UPLOAD_MAX_SIZE } from '../../../../support/shared/constants/Constants';

import { FileDesc } from '../../../../types/shared/FileDesc';
import {
  validateInput,
  validateRealTime,
} from '../TextInputElement/validators';
import {
  DocumentTypes,
  ImageTypes,
} from '../../../../core/constants/MimeTypes';

type Props = {
  definition: {
    [key: string]: any;
  };
  onChange: (file: null | FileDesc) => any;
  onValidityChange?: (
    id: string,
    validationError: string | null | undefined,
  ) => void;
  readOnly?: boolean;
  selectedFile: FileDesc | null | undefined;
};

type State = {
  inputValue: string;
  showError: string | null | undefined;
  upload: FileUpload | null | undefined;
  uploadProgress: number;
};

const imageTypes = getTypeList(ImageTypes);
const documentTypes = getTypeList(DocumentTypes);

export default class ControlledFormView extends Component<Props, State> {
  state: State = {
    inputValue: this.props.selectedFile?.expiryValue ?? '',
    showError: null,
    upload: null,
    uploadProgress: 0,
  };

  componentWillUnmount() {
    this._cancelUpload();
  }

  render() {
    if (this.state.upload) {
      return this._renderProgress();
    }
    let { selectedFile, definition } = this.props;
    return (
      <VStack alignItems="stretch">
        {selectedFile ? (
          <Thumbnail width={180} height={135} file={selectedFile} />
        ) : null}
        <HStack>
          {this._renderErrorMessage()}
          <Tooltip label={t('20MB file size limit')}>
            <HStack>
              <FilePickerButton
                id={definition?.id}
                variant="outline"
                onFileSelect={this._onFileSelect}
                inputProps={{
                  accept: definition.image_only ? imageTypes : documentTypes,
                }}
              >
                {t('Choose File')}
              </FilePickerButton>
            </HStack>
          </Tooltip>
          {selectedFile ? this._renderNameInput(selectedFile) : null}
        </HStack>
        {definition.includeExpiry && (
          <HStack>
            <Text>{t('Expiry: ')}</Text>
            <Input
              placeholder={
                definition.field_validation === 'Date_Intl'
                  ? 'DD/MM/YYYY'
                  : 'MM/DD/YYYY'
              }
              value={this.state.inputValue}
              onChange={(event) => this._onInputChange(event.target.value)}
              onBlur={this._onInputBlur}
              disabled={!this.props.selectedFile}
              borderColor="grey"
            />
          </HStack>
        )}
      </VStack>
    );
  }

  _renderNameInput = (selectedFile: FileDesc) => {
    let { readOnly } = this.props;
    return (
      <InputGroup size="md">
        <Input
          key={selectedFile.id}
          value={selectedFile.name}
          pr="4.5rem"
          type="text"
          onChange={(event) => {
            let newName = event.target.value;
            let { selectedFile } = this.props;
            if (selectedFile) {
              this.props.onChange({ ...selectedFile, name: newName });
            }
          }}
          tabIndex={readOnly ? -1 : undefined}
        />
        <InputRightElement width="4.5rem">
          <Button h="1.75rem" size="sm" onClick={this._clearFile}>
            <Icon name="xMark" size="S" />
          </Button>
        </InputRightElement>
      </InputGroup>
    );
  };

  _renderErrorMessage = () => {
    let { showError } = this.state;
    if (showError == null) {
      return;
    }
    return (
      <Alert
        type="error"
        text={showError}
        style={{ maxWidth: 300, marginRight: 15 }}
        onDismiss={this._dismissAlert}
      />
    );
  };

  _renderProgress = () => {
    let tabIndex = this.props.readOnly ? -1 : undefined;
    let { uploadProgress } = this.state;
    let percent = Math.round(uploadProgress * 1000) / 10;
    return (
      <HStack>
        <Progress
          value={percent}
          hasStripe
          style={{
            width: 210,
            marginRight: 8,
          }}
        />
        <Button onClick={this._cancelUpload} tabIndex={tabIndex}>
          {t('Cancel Upload')}
        </Button>
      </HStack>
    );
  };

  _dismissAlert = () => {
    this.setState({ showError: null });
  };

  _clearFile = () => {
    this.props.onChange(null);
    this.setState({ inputValue: '' });
  };

  _onFileSelect = (files: Array<File>) => {
    this.setState({ showError: null });
    let file = files[0];
    if (file) {
      this._attemptFileUpload(file);
    }
  };

  _attemptFileUpload = (file: File) => {
    let { definition } = this.props;
    let isValidImage = file.type in ImageTypes;
    if (definition.image_only && !isValidImage) {
      this.setState({
        showError: t('Error: Invalid file type. Please select an image.'),
      });
      return;
    }
    if (file.size > UPLOAD_MAX_SIZE) {
      this.setState({ showError: t('Error: File exceeds maximum size.') });
      return;
    }
    let url = definition.image_only ? '/api/images' : '/api/files';
    let upload = new FileUpload(url, file, { errorEarly: true });
    upload.addListener('progress', this._onUploadProgress);
    upload.addListener('complete', this._onUploadComplete);
    this.setState({
      upload: upload,
      uploadProgress: 0,
    });
    upload.send();
  };

  _onUploadProgress = (loaded: number, total: number) => {
    let progress = loaded / total;
    this.setState({ uploadProgress: progress });
  };

  _onUploadComplete = (error: Error | null, response: Response) => {
    let selectedFile: FileDesc | null = null;
    if (error) {
      let message = t('Upload failed: {message}.', { message: error.message });
      this._showError(message, error);
    } else if (response.body && response.body.error) {
      this._showError(response.body.error);
    } else if (response.status !== 200) {
      this._showError(
        t('Upload failed: HTTP Status {status}', { status: response.status }),
      );
    } else {
      let data = response.body || {};
      let { upload } = this.state;
      if (upload && typeof data.id === 'string') {
        selectedFile = {
          id: data.id,
          name: upload.file.name,
          size: upload.file.size,
          type: upload.file.type,
          imageType: null,
          width: null,
          height: null,
        };
      }

      if (
        selectedFile &&
        typeof data.imageType === 'string' &&
        typeof data.width === 'number' &&
        typeof data.height === 'number'
      ) {
        Object.assign(selectedFile, {
          imageType: data.imageType,
          width: data.width,
          height: data.height,
        });
      }
    }
    this.setState({ upload: null });
    this.props.onChange(selectedFile);
  };

  _showError = (
    message: string,
    error?: {
      [key: string]: any;
    },
  ) => {
    this.setState({ showError: message });
    logError(message, error);
  };

  _cancelUpload = () => {
    let { upload } = this.state;
    if (upload != null) {
      upload.abort();
      this.setState({ inputValue: '', upload: null });
    }
  };

  _onInputBlur = (event: any) => {
    if (!this.props.selectedFile) {
      if (this.state.inputValue !== '') {
        this.setState({ showError: t('Please upload a file first') });
        this._renderErrorMessage();
      }
      return;
    }
    let { value } = event.target;
    let { definition, onChange, onValidityChange } = this.props;
    if (definition.type === 'FileUploadElement') {
      if (onChange != null) {
        onChange({
          ...this.props.selectedFile,
          expiryValue: this.state.inputValue,
        });
      }
      if (onValidityChange) {
        let validationError = validateInput(definition.field_validation, value);
        onValidityChange(definition.id, validationError);
      }
    }
  };

  _onInputChange = (value: string) => {
    let { definition, onValidityChange, selectedFile } = this.props;
    if (!selectedFile) {
      this.setState({ showError: t('Please upload a file first') });
      this._renderErrorMessage();
      return;
    }
    if (definition.type === 'FileUploadElement') {
      value = validateRealTime(definition.field_validation, value);
    }
    // Disabled until we can make render faster for large forms. With this
    // turned on, every keystroke will cause the a state change in FormEmbed,
    // causing a re-render. That should be fine except it's taking over 300ms
    // to render in some cases.
    // if (onChange != null) {
    //   onChange(definition.id, value);
    // }
    if (onValidityChange != null) {
      onValidityChange(definition.id, null);
    }
    this.setState({ inputValue: value });
  };
}

function logError(...args: any) {
  console.error(...args); // eslint-disable-line no-console
}

export function getTypeList(contentTypes: ObjectOf<string>) {
  let set = new Set();
  for (let contentType of Object.keys(contentTypes)) {
    set.add(contentType);
    let ext = contentTypes[contentType];
    set.add('.' + ext);
  }
  return Array.from(set).join(',');
}
